import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Toast } from "react-vant";
import styles from "./MyGift.module.less";
import CommonNavBar from "views/components/CommonNavBar";
import { myCouponList } from "@/api/mine";
import useScrollLoad from "utils/useScrollLoad";

const TABS = {
  UNUSED: "1",
  USED: "2",
  EXPIRED: "3",
};

const tabsList = [
  { value: TABS.UNUSED, label: "待使用" },
  { value: TABS.USED, label: "已使用" },
  { value: TABS.EXPIRED, label: "已过期" },
];

const loadingText = {
  nomore: "已经到底啦～",
  loading: "正在加载～",
  empty: "暂无数据",
};

const checkIsDue = (endTimeStr) => {
  try {
    const dateStr = endTimeStr.replace(/\./g, "-").replace(/\/g/, "-");

    const endTime = new Date(dateStr);
    if (isNaN(endTime.getTime())) {
      console.warn(`Invalid date format: ${endTimeStr}`);
      return false;
    }

    const diffTime = endTime.getTime() - new Date().getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 7 && diffDays >= 0;
  } catch (error) {
    console.warn(`Error processing date: ${endTimeStr}`, error);
    return false;
  }
};

const MyGift = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(TABS.UNUSED);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalCount: 0,
  });
  const [loading, setLoading] = useState(true);
  const [dataList, setDataList] = useState([]);
  const loadingRef = useRef(null);
  const isFirstRender = useRef(true);

  const getList = async (page) => {
    if (page === 1) {
      setDataList([]);
    }

    setLoading(true);
    loadingRef.current = Toast.loading({
      message: "正在加载数据...",
      forbidClick: true,
      duration: 0,
    });

    try {
      const res = await myCouponList(activeTab, page);
      if (res.code === 200) {
        const list = res.data?.data || [];
        list.forEach((item) => {
          item.open = false;
          item.isDue = checkIsDue(item.use_end_time);
        });

        setDataList((prev) => (page === 1 ? list : [...prev, ...list]));
        setPagination({
          currentPage: page + 1,
          totalCount: res.data.total_count || 0,
        });
      }
    } finally {
      if (loadingRef.current?.clear) {
        loadingRef.current.clear();
      }
      setLoading(false);
    }
  };

  const handleDropClick = (index) => {
    if (dataList[index].goods_name) {
      setDataList((prev) =>
        prev.map((item, i) =>
          i === index ? { ...item, open: !item.open } : item
        )
      );
    } else {
      Toast.info("暂无可用商品");
    }
  };

  const containerRef = useScrollLoad(() => {
    if (dataList.length < pagination.totalCount && !loading) {
      getList(pagination.currentPage);
    }
  });

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    getList(1);
    return () => {
      if (loadingRef.current?.clear) {
        loadingRef.current.clear();
      }
    };
  }, [activeTab]);

  useEffect(() => {
    getList(1);
    return () => {
      if (loadingRef.current?.clear) {
        loadingRef.current.clear();
      }
    };
  }, []);

  const renderLeftTime = (item) => {
    switch (activeTab) {
      case TABS.UNUSED:
        return (
          <div className={styles["left-time"]}>
            {`有效期：${item.use_start_time.split(" ")[0]} - ${
              item.use_end_time.split(" ")[0]
            }`}
          </div>
        );
      case TABS.USED:
        return (
          <div className={styles["left-time"]}>{`兑换时间：${
            item.use_time.split(" ")[0]
          }`}</div>
        );
      case TABS.EXPIRED:
        return (
          <div
            className={`${styles["left-time"]} ${styles["left-time--disabled"]}`}
          >
            {`有效期：${item.use_start_time.split(" ")[0]} - ${
              item.use_end_time.split(" ")[0]
            }`}
          </div>
        );
      default:
        return null;
    }
  };
  const renderLeftDrop = (item, index) => {
    switch (activeTab) {
      case TABS.UNUSED:
        return (
          <div
            className={styles["left-drop"]}
            onClick={() => handleDropClick(index)}
          >
            <span className={styles["left-drop-text"]}>{"查看可用商品"}</span>
            <span
              className={`${styles["left-drop-icon"]} ${
                !item.open ? styles["left-drop-icon--open"] : ""
              }`}
            >
              {"^"}
            </span>
          </div>
        );
      case TABS.USED:
        return (
          <div className={styles["left-drop"]}>
            <span className={styles["left-drop-text"]}>
              {`兑换商品：${item.coupon_goods_name}`}
            </span>
          </div>
        );
      case TABS.EXPIRED:
        return (
          <div
            className={`${styles["left-drop-text"]} ${styles["left-drop-text--disabled"]}`}
          >
            {"该券已无法兑换"}
          </div>
        );
      default:
        return null;
    }
  };

  const renderRightButton = (item) => {
    if (activeTab === TABS.UNUSED) {
      return (
        <div
          // className={`${styles["right-button"]} ${
          //   item.isDue ? styles["right-button--due"] : ""
          // }`}
          className={styles["right-button"]}
          onClick={() =>
            navigate(`/mine/pointsMall?coupon_id=${item.coupon_id}`)
          }
        >
          {"立即使用"}
        </div>
      );
    }

    return (
      <div
        className={`${styles["right-button"]} ${styles["right-button--disabled"]}`}
      >
        {tabsList[Number(activeTab) - 1].label}
      </div>
    );
  };

  return (
    <div className={styles["my-gift-container"]}>
      <CommonNavBar onClickBack={() => navigate(-1)} title="我的礼券" />
      <img
        className={styles["right-banner"]}
        src={require("@/images/mine/img_integration_top_bg.png")}
      />
      <div className={styles["tabs-box"]}>
        {tabsList.map((item) => (
          <div
            key={item.value}
            className={`${styles["tabs-box-item"]} ${
              activeTab === item.value ? styles["tabs-box-item--active"] : ""
            }`}
            onClick={() => setActiveTab(item.value)}
          >
            {item.label}
          </div>
        ))}
      </div>
      <div className={styles["list-box"]} ref={containerRef}>
        {dataList.map((item, index) => (
          <div className={styles["list-box-item"]} key={`my-gift-${index}`}>
            <div className={styles["item-top"]}>
              <div className={styles["item-top-left"]}>
                <div className={styles["left-title"]}>{item.name}</div>
                {renderLeftTime(item)}
                {renderLeftDrop(item, index)}
              </div>
              <div className={styles["item-top-right"]}>
                {renderRightButton(item)}
                {item.isDue && activeTab === TABS.UNUSED && (
                  <img
                    className={styles["right-due-img"]}
                    src={require("@/images/mine/my-gift/due.png")}
                  />
                )}
              </div>
            </div>
            <div
              className={styles["item-bottom"]}
              style={{ display: item.open ? "block" : "none" }}
            >
              {item.goods_name}
            </div>
          </div>
        ))}
        <div className={styles["list-bottom"]}>
          {loading
            ? loadingText.loading
            : dataList.length === 0
            ? loadingText.empty
            : loadingText.nomore}
        </div>
      </div>
    </div>
  );
};

export default MyGift;
